import { isPending, type MetricValue } from '@fleetcore/shared'

import { cn } from '../../lib/cn'
import { usePendingLabel } from './pending-label'

/**
 * Rising bars for a quality reading — GSM signal strength, GNSS fix quality.
 *
 * The unfilled bars are drawn, not omitted. Three bars out of five and three bars
 * out of three are very different facts about a link, and a component that only
 * draws what is lit cannot express the difference.
 */

const BAR_GAP = 3

export interface SignalGaugeProps {
  label: string
  /** How many bars are lit. Clamped into [0, bars]. */
  metric: MetricValue<number>
  bars?: number
  /** Optional text beside the label — "3/5", "9 satellites". */
  valueText?: string
  hint?: string
  /** Resolved sentence replacing the generic pending wording — see <ArcGauge />. */
  pendingText?: string
  className?: string
}

export function SignalGauge({
  label,
  metric,
  bars = 5,
  valueText,
  hint,
  pendingText,
  className,
}: SignalGaugeProps) {
  const pendingLabel = usePendingLabel()
  const pending = isPending(metric)

  const lit = pending ? 0 : Math.min(bars, Math.max(0, Math.round(metric.value)))
  const text = pending ? (pendingText ?? pendingLabel(metric)) : (valueText ?? `${lit}/${bars}`)
  // One bar out of five is not "some signal", it is a link about to drop.
  const weak = !pending && lit > 0 && lit / bars <= 0.25

  const width = bars * 10 + (bars - 1) * BAR_GAP

  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <svg
        role="img"
        aria-label={`${label} : ${text}`}
        viewBox={`0 0 ${width} 24`}
        className="h-5 shrink-0"
        style={{ width: `${width}px` }}
      >
        {Array.from({ length: bars }, (_, index) => {
          const height = 8 + (index * 16) / Math.max(1, bars - 1)
          const on = index < lit
          return (
            <rect
              key={index}
              x={index * (10 + BAR_GAP)}
              y={24 - height}
              width={10}
              height={height}
              rx={2}
              className={cn(
                'fill-current',
                on ? (weak ? 'text-warning' : 'text-accent') : 'text-surface-muted',
              )}
            />
          )
        })}
      </svg>

      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-foreground">{label}</p>
        <p
          className={cn(
            'text-xs tabular-nums',
            // An explained absence is a sentence and must be readable in full;
            // truncating it to "Données CAN non dispo…" defeats the explanation.
            pending
              ? 'text-foreground-subtle'
              : cn('truncate', weak ? 'text-warning' : 'text-foreground-muted'),
          )}
        >
          {text}
        </p>
        {!pending && hint ? (
          <p className="truncate text-xs text-foreground-subtle">{hint}</p>
        ) : null}
      </div>
    </div>
  )
}
